import ReactDOMServer from 'react-dom/server';
import uPlot from 'uplot';
import { makeStyles, Theme } from '@material-ui/core';
import { roundNumber } from '@shared/utils';
import { ChannelSummaryModel } from '@shared/models';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { MAINTENANCE_GRAPH_SCALE_NAME } from '@features/GraphsView/MaintenanceGraphs/components/MaintenanceGraph';
import { ACTUATIONS_GRAPH_SCALE_NAME } from '@features/GraphsView/Actuations/Actuations';
import { getNiceTickValues } from 'recharts-scale';

export const graphColors = [
  '#800080',
  '#4ECDC4',
  '#ff6347',
  '#ffD700',
  '#8A2BE2',
  '#808000',
  '#03A9F4',
  '#bbff00',
  '#622be2',
];

export const CustomChannelNamesFn = (): { name: string; color: string }[] => [
  { name: t`PRV Inlet`, color: '#cc0000' },
  { name: t({ message: 'PRV', id: '[SiteType.PRV]' }), color: '#009205' },
  { name: t`PRV Outlet`, color: 'blue' },
  { name: t`PRV Top`, color: 'orange' },
];

export const mainGraphChannelLabelAndColor = (
  address: string,
  channelSummary: ChannelSummaryModel,
  colorIterator: number,
  isPrv: boolean
) => {
  const { channelName, channelNo, channelType } = channelSummary;

  const isFlowChannel = channelType === 'Flow';
  const isPressureChannel = channelType === 'Pressure';

  const colorIndex = colorIterator % graphColors.length;

  const CustomChannelNames = CustomChannelNamesFn();

  const color =
    isPrv && channelNo <= CustomChannelNames.length ? CustomChannelNames[channelNo - 1].color : graphColors[colorIndex];

  const channelNameToDisplay =
    isFlowChannel || isPressureChannel
      ? isPrv
        ? channelNo <= CustomChannelNames.length
          ? CustomChannelNames[channelNo - 1].name
          : channelName ?? ''
        : t`Critical Point`
      : channelName ?? '';

  const channelTypeTranslated = isFlowChannel
    ? t({ message: 'Flow', id: '[PegasusTable.Flow]' })
    : isPressureChannel
      ? t({ message: 'Pressure', id: '[PegasusTable.Pressure]' })
      : channelType;

  const channelLabelName = `${address ? `${address} -` : ''} ${channelNameToDisplay} ${channelTypeTranslated}`;

  return { label: channelLabelName, color };
};

export const useGraphStyles = makeStyles((theme: Theme) => ({
  hiddenSeries: {
    display: 'none !important',
    width: 0,
    height: 0,
  },
}));

export const tooltipPlugin = (
  smallScreen: boolean,
  formatDate: (date: string | Date, dateFormat?: string) => string
) => {
  const TOOLTIP_HEIGHT = 60;
  const TOOLTIP_WIDTH = smallScreen ? 136 : 232;
  const TOOLTIP_DISPLAY_CORRECTION = 16;

  const init = (u: uPlot, opts: uPlot.Options) => {
    const over = u.over;

    over.style.cursor = 'pointer';

    (u as uPlot & { tooltips: HTMLDivElement[] }).tooltips = opts.series.map((s, i) => {
      if (i !== 0) {
        const color = s.stroke as string;
        const tooltip = document.createElement('div');
        tooltip.className = 'pointer-events-none absolute bg-white px-0.5 rounded';
        tooltip.textContent = '';
        tooltip.style.zIndex = '9999';
        tooltip.style.color = color;
        tooltip.style.fontSize = '12px';
        tooltip.style.border = `1px solid ${color}`;
        tooltip.style.maxWidth = `${TOOLTIP_WIDTH}px`;
        over.appendChild(tooltip);
        return tooltip;
      } else {
        return null;
      }
    });

    const hideTips = () => {
      (u as uPlot & { tooltips: HTMLDivElement[] }).tooltips?.forEach((el, i) => {
        if (i !== 0) {
          el.style.display = 'none';
        }
      });
    };

    const showTips = () => {
      (u as uPlot & { tooltips: HTMLDivElement[] }).tooltips?.forEach((el, i) => {
        if (i !== 0) {
          el.style.display = u.series[i]?.show && !u.data[i]?.some((el) => el !== undefined) ? null : 'none';
        }
      });
    };

    over.addEventListener('mouseleave', () => {
      if (!(u.cursor as uPlot.Cursor & { _lock: boolean })._lock) {
        hideTips();
      }
    });

    over.addEventListener('mouseenter', (e) => {
      showTips();
    });

    hideTips();
  };

  function setCursor(u: uPlot) {
    const { idx, top, left } = u.cursor;

    const tooltips = (u as uPlot & { tooltips: HTMLDivElement[] }).tooltips;

    const dateVal = u.data[0][idx];

    const date = dateVal ? formatDate(new Date(dateVal * 1000)) : '';
    const graphWidth = u.over.offsetWidth;

    tooltips?.forEach((el, i) => {
      if (i !== 0) {
        const yVal = u.data[i][idx];
        const series = u.series[i];

        if (series.show && series.class !== 'boundary' && series.width > 0) {
          const xVal = u.data[0][idx];

          const leftPosition = Math.round(u.valToPos(xVal, 'x'));
          const topPosition = Math.round(u.valToPos(yVal, series.scale));

          const showTooltip =
            Number.isFinite(yVal) &&
            top > topPosition - 8 &&
            top < topPosition + 8 &&
            left > leftPosition - 8 &&
            left < leftPosition + 8;

          el.innerHTML = showTooltip
            ? ReactDOMServer.renderToString(
                <div className="my-0.5 pl-1">
                  <p style={{ fontFamily: 'Ubuntu Sans', fontSize: 12.5, marginRight: 4 }}>{series.label}</p>
                  <p style={{ fontFamily: 'Ubuntu Sans', fontSize: 12.5 }}>{date}</p>
                  <div className="flex flex-wrap">
                    <p style={{ fontFamily: 'Ubuntu Sans', fontSize: 12.5 }}>
                      {(series.class?.replaceAll('_', ' ') ?? series.scale).replace('Â', '')}: {roundNumber(yVal, 3)}
                    </p>
                  </div>
                </div>
              )
            : null;

          const lefPosVal = smallScreen
            ? leftPosition < TOOLTIP_WIDTH / 4
              ? leftPosition
              : graphWidth - leftPosition < TOOLTIP_WIDTH / 4
                ? leftPosition - TOOLTIP_WIDTH
                : leftPosition - TOOLTIP_WIDTH / 2
            : graphWidth - leftPosition < TOOLTIP_WIDTH
              ? leftPosition - (TOOLTIP_WIDTH + TOOLTIP_DISPLAY_CORRECTION)
              : leftPosition + TOOLTIP_DISPLAY_CORRECTION;

          const topPosFactor = smallScreen ? 0 : TOOLTIP_DISPLAY_CORRECTION;
          const topPosVal =
            topPosition - (topPosition < TOOLTIP_HEIGHT ? -topPosFactor : TOOLTIP_HEIGHT + topPosFactor);

          el.style.minWidth = TOOLTIP_WIDTH + 'px';
          el.style.width = smallScreen ? TOOLTIP_WIDTH + 'px' : null;
          el.style.display = showTooltip ? null : 'none';
          el.style.left = lefPosVal + 'px';
          el.style.top = topPosVal + 'px';
        }
      }
    });
  }

  return {
    hooks: {
      init,
      setCursor,
    },
  };
};

const SIGN_WIDTH = 9;
const ADDITIONAL_FACTOR = 14;
const DOT_WIDTH = 3.5;
const ADDITIONAL_DOT_FACTOR = ADDITIONAL_FACTOR + DOT_WIDTH;

export const axesWidthPlugin = () => {
  const setScale = (u: uPlot, scaleKey: string) => {
    const axis = u.axes?.find((a) => a.scale === scaleKey && scaleKey !== 'x' && scaleKey !== 'y');

    if (Boolean(axis)) {
      axis.filter = (_, splits) => {
        axis.size = () => {
          // max width of a scale numbers without dot (.)
          const width = Math.max(
            ...(splits?.map((s) => {
              const roundTo =
                scaleKey === ACTUATIONS_GRAPH_SCALE_NAME || scaleKey === MAINTENANCE_GRAPH_SCALE_NAME ? 1 : 100;
              const roundedSplitValue = Math.round(s * roundTo) / roundTo;
              return roundedSplitValue.toString().replace('.', '').length ?? 0;
            }) ?? [0])
          );
          return (
            width * SIGN_WIDTH +
            (splits?.some((s) => s.toString().includes('.')) ? ADDITIONAL_DOT_FACTOR : ADDITIONAL_FACTOR)
          );
        };
        return splits;
      };
    }
  };

  return {
    hooks: {
      setScale,
    },
  };
};

export const TICKS_COUNT = 9;

export const axesDynamicScalesPlugin = (
  scalesState?: uPlot.Scales,
  setScalesState?: (scales: uPlot.Scales) => void
) => {
  const setData = (u: uPlot) => {
    const axesList = u.axes.filter((axis) => axis.scale !== 'x' && axis.scale !== 'y');

    axesList.forEach((axis) => {
      const canSetRangeForScale = u.series.filter((s) => s.scale === axis.scale).some((s) => s.show);

      if (canSetRangeForScale) {
        u.scales[axis.scale].range = (_: uPlot, initMin: number, initMax: number) => {
          if ((initMin === 0 || initMin === null) && initMin === initMax) {
            return uPlot.rangeNum(-1, 1, 0, true);
          }

          const noNegativeValues = initMin >= 0;
          const negativeOrZeroMax = initMax <= 0;

          const min = noNegativeValues ? 0 : initMin;
          const max = negativeOrZeroMax ? Math.abs(initMin / 2) : initMax;

          const ticks = getNiceTickValues([min, max], TICKS_COUNT, true);

          return [ticks[0], ticks[TICKS_COUNT - 1]];
        };
      }
    });

    const shouldHideActuationsAxis = u.series.some((s) => s.scale === ACTUATIONS_GRAPH_SCALE_NAME && s.width === 0);

    if (shouldHideActuationsAxis) {
      u.axes.find((a) => a.scale === ACTUATIONS_GRAPH_SCALE_NAME).show = false;
    }

    // keep zoomed in after toggling data checkboxes
    // hide axes with no data (no Flow and Pressure)
    if (scalesState) {
      const axesToHide = Object.keys(scalesState).filter(
        (key) =>
          key !== 'Flow' &&
          key !== 'Pressure' &&
          key !== ACTUATIONS_GRAPH_SCALE_NAME &&
          !u.series.some((s) => s.scale === key && s.show)
      );
      axesToHide.forEach((axisScale) => {
        const axis = u.axes.find((axis) => axis.scale === axisScale);
        if (axis) {
          axis.show = false;
        }
      });

      const scales = Object.entries(scalesState).filter(([key]) => u.series.some((s) => s.scale === key));

      scales?.forEach(([key, scale]) => {
        u.setScale(key, { min: scale.min, max: scale.max });
      });
    }
  };

  const setScale = (u: uPlot, scaleKey: string) => {
    const axis = u.axes?.find((a) => a.scale === scaleKey && scaleKey !== 'x' && scaleKey !== 'y');

    if (axis && axis?.show) {
      axis.splits = (_: uPlot, __: number, scaleMin: number, scaleMax: number) => {
        const ticks = getNiceTickValues([scaleMin, scaleMax], TICKS_COUNT, true);

        if (ticks[0] !== scaleMin || ticks[TICKS_COUNT - 1] !== scaleMax) {
          queueMicrotask(() => {
            u.setScale(scaleKey, { min: ticks[0], max: ticks[TICKS_COUNT - 1] });
          });
        }

        return ticks;
      };

      axis.values = (_, splits) => {
        if (scaleKey === ACTUATIONS_GRAPH_SCALE_NAME || scaleKey === MAINTENANCE_GRAPH_SCALE_NAME) {
          return splits?.map((val) => Math.round(val * 1) / 1);
        }

        return splits?.map((val) => {
          if (val % 1 !== 0 && Math.abs(val) < 10) {
            return val.toFixed(2);
          }

          if (val % 1 !== 0 && Math.abs(val) < 1000) {
            return val.toFixed(1);
          }

          return val.toFixed(0);
        });
      };
    }

    if (scaleKey === 'x' && setScalesState) {
      setScalesState(u.scales);
    }
  };

  return {
    hooks: {
      setData,
      setScale,
    },
  };
};

export const NUMBER_OF_BOUNDARIES = 3;

export enum ShowBoundLineType {
  TOP = 'TOP', // display tooltip at the top
  BOTTOM = 'BOTTOM', // display tooltip at the bottom
  DEFAULT = 'DEFAULT', // display tooltip on the dashed line
  NONE = 'NONE',
}

export interface BoundsPluginParams {
  label: string;
  scale: string;
  show: ShowBoundLineType[];
  values: number[]; // [lowerVal, upperVal] -> value could be null
}

const SINGLE_HEIGHT = 38;
const SINGLE_HEIGHT_ARR = 58;
const COMBINED_HEIGHT = 52;
const WIDTH = 100;
const SMALL_WIDTH = 80;

export const boundsPlugin = (smallScreen: boolean, data: BoundsPluginParams[]) => {
  if (!data || isEmpty(data)) return { hooks: null };

  const classes = `flex flex-col justify-center items-center pointer-events-none absolute bg-white p-1 rounded opacity-85 transform -translate-x-1/2`;

  const leftPositionCalc = (i: number) =>
    (smallScreen ? SMALL_WIDTH : WIDTH) / 2 + i * (smallScreen ? SMALL_WIDTH : WIDTH);

  const init = (u: uPlot, opts: uPlot.Options) => {
    const over = u.over;

    const bandsTooltipsComponents = Array(NUMBER_OF_BOUNDARIES)
      .fill(null)
      .map((_, i) => {
        const upperBound = document.createElement('div');
        const lowerBound = document.createElement('div');
        over.appendChild(upperBound);
        over.appendChild(lowerBound);

        return [upperBound, lowerBound];
      });

    (u as uPlot & { boundsTooltips: HTMLDivElement[][] }).boundsTooltips = bandsTooltipsComponents;

    const showBounds = () => {
      (u as uPlot & { boundsTooltips: HTMLDivElement[][] }).boundsTooltips?.forEach((tooltips) =>
        tooltips.forEach((tooltip) => (tooltip.style.display = null))
      );
    };

    showBounds();
  };

  const draw = (u: uPlot) => {
    const graphHeight = u.over.clientHeight;

    data?.forEach(({ label, scale, show, values }, i) => {
      const [upperVal, lowerVal] = values;
      const [showUpperBoundLine, showLowerBoundLine] = show;

      const upperTopPos = Math.round(u.valToPos(upperVal, scale)) ?? 0;
      const lowerTopPos = Math.round(u.valToPos(lowerVal, scale)) ?? 0;

      const series = u.series.find((s) => s.class !== 'boundary' && s.scale === scale);

      const bothTooltipsCanBeDisplayed =
        showUpperBoundLine !== ShowBoundLineType.NONE && showLowerBoundLine !== ShowBoundLineType.NONE;

      const bothBoundsOverTheGraph =
        (upperTopPos < 0 && lowerTopPos < 0) ||
        (showUpperBoundLine === ShowBoundLineType.TOP && showLowerBoundLine === ShowBoundLineType.TOP);
      const bothBoundsUnderTheGraph =
        (upperTopPos > graphHeight && lowerTopPos > graphHeight) ||
        (showUpperBoundLine === ShowBoundLineType.BOTTOM && showLowerBoundLine === ShowBoundLineType.BOTTOM);

      const upperOverGraphLowerCloseToTop = upperTopPos < 0 && lowerTopPos < SINGLE_HEIGHT_ARR + SINGLE_HEIGHT / 2;
      const lowerUnderGraphUpperCloseToBottom =
        lowerTopPos > graphHeight && graphHeight - upperTopPos < SINGLE_HEIGHT_ARR + SINGLE_HEIGHT / 2;

      const showAsOneTooltip =
        bothTooltipsCanBeDisplayed &&
        (bothBoundsOverTheGraph ||
          bothBoundsUnderTheGraph ||
          upperOverGraphLowerCloseToTop ||
          lowerUnderGraphUpperCloseToBottom);

      const overlappingTooltips = bothTooltipsCanBeDisplayed && lowerTopPos - upperTopPos < SINGLE_HEIGHT;

      if (Boolean(series)) {
        const componentsToDisplay: string[] = [];
        if (showAsOneTooltip || overlappingTooltips) {
          componentsToDisplay.push(null); // need to have 2 components

          componentsToDisplay.push(
            ReactDOMServer.renderToString(
              <div
                className={classes}
                style={{
                  left: `${leftPositionCalc(i)}px`,
                  ...(showAsOneTooltip
                    ? bothBoundsOverTheGraph || upperOverGraphLowerCloseToTop
                      ? { top: 0 }
                      : bothBoundsUnderTheGraph || lowerUnderGraphUpperCloseToBottom
                        ? { bottom: 0 }
                        : {}
                    : overlappingTooltips // always both displayed within the graph space
                      ? upperTopPos < COMBINED_HEIGHT / 2
                        ? { top: 0 }
                        : graphHeight - upperTopPos < COMBINED_HEIGHT / 2
                          ? { bottom: 0 }
                          : {
                              top: (upperTopPos + lowerTopPos) / 2 - COMBINED_HEIGHT / 2,
                            }
                      : {}),
                  maxWidth: smallScreen ? SMALL_WIDTH : WIDTH * 2,
                }}
              >
                {showUpperBoundLine === ShowBoundLineType.TOP ||
                bothBoundsOverTheGraph ||
                upperOverGraphLowerCloseToTop ? (
                  <ArrowUpward fontSize="small" />
                ) : null}
                {Number.isFinite(upperVal) ? (
                  <p style={{ fontFamily: 'Ubuntu Sans', fontSize: smallScreen ? 9 : 10 }}>
                    Upper{smallScreen ? '' : ' level'}: {upperVal}
                  </p>
                ) : null}
                {Number.isFinite(lowerVal) ? (
                  <p style={{ fontFamily: 'Ubuntu Sans', fontSize: smallScreen ? 9 : 10 }}>
                    Lower{smallScreen ? '' : ' level'}: {lowerVal}
                  </p>
                ) : null}
                <p
                  style={{
                    fontFamily: 'Ubuntu Sans',
                    textAlign: 'center',
                    fontSize: smallScreen ? 8 : 9,
                  }}
                >
                  {label}
                </p>
                {showUpperBoundLine === ShowBoundLineType.BOTTOM ||
                bothBoundsUnderTheGraph ||
                lowerUnderGraphUpperCloseToBottom ? (
                  <ArrowDownward fontSize="small" />
                ) : null}
              </div>
            )
          );
        } else {
          componentsToDisplay.push(
            Number.isFinite(upperVal)
              ? ReactDOMServer.renderToString(
                  <div
                    className={classes}
                    style={{
                      left: `${leftPositionCalc(i)}px`,
                      top:
                        showUpperBoundLine === ShowBoundLineType.TOP || upperTopPos <= SINGLE_HEIGHT / 2
                          ? 0
                          : showUpperBoundLine === ShowBoundLineType.DEFAULT &&
                              graphHeight - upperTopPos > SINGLE_HEIGHT / 2
                            ? upperTopPos - SINGLE_HEIGHT / 2
                            : null,
                      bottom:
                        showUpperBoundLine === ShowBoundLineType.BOTTOM ||
                        graphHeight - upperTopPos <= SINGLE_HEIGHT / 2
                          ? 0
                          : null,
                      maxWidth: smallScreen ? SMALL_WIDTH : WIDTH * 2,
                    }}
                  >
                    {(showUpperBoundLine === ShowBoundLineType.TOP || upperTopPos < 0) && (
                      <ArrowUpward fontSize="small" />
                    )}
                    <p style={{ fontFamily: 'Ubuntu Sans', fontSize: smallScreen ? 9 : 10 }}>
                      Upper{smallScreen ? '' : ' level'}: {upperVal}
                    </p>
                    <p
                      style={{
                        fontFamily: 'Ubuntu Sans',
                        textAlign: 'center',
                        fontSize: smallScreen ? 8 : 9,
                      }}
                    >
                      {label}
                    </p>
                    {(showUpperBoundLine === ShowBoundLineType.BOTTOM || upperTopPos > graphHeight) && (
                      <ArrowDownward fontSize="small" />
                    )}
                  </div>
                )
              : null
          );

          componentsToDisplay.push(
            Number.isFinite(lowerVal)
              ? ReactDOMServer.renderToString(
                  <div
                    className={classes}
                    style={{
                      left: `${leftPositionCalc(i)}px`,
                      top:
                        showLowerBoundLine === ShowBoundLineType.TOP || lowerTopPos <= SINGLE_HEIGHT / 2
                          ? 0
                          : showLowerBoundLine === ShowBoundLineType.DEFAULT &&
                              graphHeight - lowerTopPos > SINGLE_HEIGHT / 2
                            ? lowerTopPos - SINGLE_HEIGHT / 2
                            : null,
                      bottom:
                        showLowerBoundLine === ShowBoundLineType.BOTTOM ||
                        graphHeight - lowerTopPos <= SINGLE_HEIGHT / 2
                          ? 0
                          : null,
                      maxWidth: smallScreen ? SMALL_WIDTH : WIDTH * 2,
                    }}
                  >
                    {(showLowerBoundLine === ShowBoundLineType.TOP || lowerTopPos < 0) && (
                      <ArrowUpward fontSize="small" />
                    )}
                    <p style={{ fontFamily: 'Ubuntu Sans', fontSize: smallScreen ? 9 : 10 }}>
                      Lower{smallScreen ? '' : ' level'}: {lowerVal}
                    </p>
                    <p
                      style={{
                        fontFamily: 'Ubuntu Sans',
                        textAlign: 'center',
                        fontSize: smallScreen ? 8 : 9,
                      }}
                    >
                      {label}
                    </p>
                    {(showLowerBoundLine === ShowBoundLineType.BOTTOM || lowerTopPos > graphHeight) && (
                      <ArrowDownward fontSize="small" />
                    )}
                  </div>
                )
              : null
          );
        }

        (u as uPlot & { boundsTooltips: HTMLDivElement[][] }).boundsTooltips[i].forEach((tooltip, i) => {
          tooltip.innerHTML = i === 0 || i === 1 ? componentsToDisplay[i] : null;
        });
      }
    });
  };

  return {
    hooks: {
      init,
      draw,
    },
  };
};

export const drawFrameOnGraph = () => {
  const drawAxes = (u: uPlot) => {
    let xLft = u.bbox.left;
    let xRgt = u.bbox.left + u.bbox.width;
    let yTop = u.bbox.top;
    let yBtm = u.bbox.top + u.bbox.height;

    u.ctx.save();
    u.ctx.beginPath();
    u.ctx.setLineDash([]);
    u.ctx.strokeStyle = '#ddd';
    u.ctx.lineWidth = 2.5;
    u.ctx.moveTo(xLft, yTop);
    u.ctx.lineTo(xLft, yBtm);
    u.ctx.lineTo(xRgt, yBtm);
    u.ctx.lineTo(xRgt, yTop);
    u.ctx.lineTo(xLft, yTop);
    u.ctx.stroke();
    u.ctx.restore();
  };

  return {
    hooks: {
      drawAxes,
    },
  };
};

export const midnightLinePlugin = () => {
  const draw = (u: uPlot) => {
    const midnightSplits = (u.axes?.[0] as uPlot & { _splits: number[] })?._splits?.filter((d) => {
      const date = new Date(d * 1000);
      return date?.getHours() === 0 && date?.getMinutes() === 0;
    });

    midnightSplits?.forEach((ms) => {
      const xLft = u.valToPos(ms, 'x', true);
      const yTop = u.bbox.top;
      const yBtm = u.bbox.top + u.bbox.height;

      u.ctx.save();
      u.ctx.beginPath();
      u.ctx.setLineDash([]);
      u.ctx.strokeStyle = '#00000029';
      u.ctx.lineWidth = 2.4;
      u.ctx.moveTo(xLft, yTop);
      u.ctx.lineTo(xLft, yBtm);
      u.ctx.stroke();
      u.ctx.restore();
    });
  };

  return {
    hooks: {
      draw,
    },
  };
};
